import React from 'react';

/**
 * One file in a medical opinion's file list, in every modal that shows files.
 *
 * The name is text, not a control: the two things a user does with a file - look at it and
 * take a copy of it - are separate icons on the right, so neither is hidden behind knowing
 * that the name happens to be clickable. The name truncates to leave the icons room.
 */
interface MedicalOpinionFileRowProps {
    filename: string;
    // Highlights the row and flips the preview icon to "hide", since previewing again closes
    // the pane.
    isSelected?: boolean;
    onPreview: () => void;
    // Omitted hides the icon - a row with nothing to download does not pretend otherwise.
    onDownload?: () => void;
    // Create-request modal only: drop a file that has not been uploaded yet.
    onRemove?: () => void;
    isDownloading?: boolean;
    disabled?: boolean;
}

const iconButtonClass =
    'shrink-0 p-1 rounded text-[#366A75] hover:text-[#2D5A60] hover:bg-[#D0E7E5] '
    + 'disabled:opacity-40 disabled:hover:bg-transparent transition-colors';

export const MedicalOpinionFileRow: React.FC<MedicalOpinionFileRowProps> = ({
    filename,
    isSelected = false,
    onPreview,
    onDownload,
    onRemove,
    isDownloading = false,
    disabled = false,
}) => (
    <li
        className={`flex items-center gap-1 text-sm rounded-lg pl-3 pr-1.5 py-1 ${
            isSelected
                ? 'bg-[#D0E7E5] text-[#2D5A60] font-medium'
                : 'bg-[#EBF5F5] text-[#366A75]'
        }`}
    >
        <span className="truncate flex-1 min-w-0" title={filename}>
            {filename}
        </span>

        <button
            data-ai-id="opinion-file-preview"
            type="button"
            onClick={onPreview}
            disabled={disabled}
            className={iconButtonClass}
            title={isSelected ? 'Hide preview' : 'Preview'}
        >
            {isSelected ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
            ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
            )}
        </button>

        {onDownload && (
            <button
                type="button"
                onClick={onDownload}
                disabled={disabled || isDownloading}
                className={iconButtonClass}
                title="Download"
            >
                {isDownloading ? (
                    <span className="block w-4 h-4 animate-spin rounded-full border-b-2 border-[#366A75]" />
                ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
                    </svg>
                )}
            </button>
        )}

        {onRemove && (
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                className={iconButtonClass}
                title="Remove"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        )}
    </li>
);
